/**
 * Pac-Man Game - Earn Pro Access by Collecting All Dots!
 */

import { Logger } from '../utils/Logger';

const logger = Logger.getLogger('PacManGame');

export interface GameResult {
  won: boolean;
  score: number;
  dotsRemaining: number;
}

export class PacManGame {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private gameLoop: number | null = null;
  private isRunning = false;
  private started = false;

  // Game Constants
  private readonly TILE_SIZE = 20;
  private readonly PACMAN_SPEED = 0.03;
  private readonly GHOST_SPEED = 0.025;
  private scale = 1;

  // Map Layout
  private mapLayout = [
    [1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1],
    [1,2,2,2,2,2,2,2,2,1,2,2,2,2,2,2,2,2,1],
    [1,3,1,1,2,1,1,1,2,1,2,1,1,1,2,1,1,3,1],
    [1,2,1,1,2,1,1,1,2,1,2,1,1,1,2,1,1,2,1],
    [1,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,1],
    [1,2,1,1,2,1,2,1,1,1,1,1,2,1,2,1,1,2,1],
    [1,2,2,2,2,1,2,2,2,1,2,2,2,1,2,2,2,2,1],
    [1,1,1,1,2,1,1,1,0,1,0,1,1,1,2,1,1,1,1],
    [0,0,0,1,2,1,0,0,0,0,0,0,0,1,2,1,0,0,0],
    [1,1,1,1,2,1,0,1,1,4,1,1,0,1,2,1,1,1,1],
    [0,2,2,2,2,0,0,1,4,4,4,1,0,0,2,2,2,2,0],
    [1,1,1,1,2,1,0,1,1,1,1,1,0,1,2,1,1,1,1],
    [0,0,0,1,2,1,0,0,0,0,0,0,0,1,2,1,0,0,0],
    [1,1,1,1,2,1,2,1,1,1,1,1,2,1,2,1,1,1,1],
    [1,2,2,2,2,2,2,2,2,1,2,2,2,2,2,2,2,2,1],
    [1,2,1,1,2,1,1,1,2,1,2,1,1,1,2,1,1,2,1],
    [1,3,2,1,2,2,2,2,2,0,2,2,2,2,2,1,2,3,1],
    [1,1,2,1,2,1,2,1,1,1,1,1,2,1,2,1,2,1,1],
    [1,2,2,2,2,1,2,2,2,1,2,2,2,1,2,2,2,2,1],
    [1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]
  ];

  private map: number[][] = [];
  private score = 0;
  private lives = 3;
  private totalDots = 0;
  private dotsEaten = 0;
  private powerMode = false;
  private frightenedTimer = 0;
  private lastChompTime = 0;

  // Audio
  private audioCtx: AudioContext | null = null;

  // Entities
  private pacman = {
    x: 9, y: 16,
    dir: {x: 0, y: 0},
    nextDir: {x: 0, y: 0},
    mouthOpen: 0,
    mouthSpeed: 0.05,
    angle: 0
  };

  private ghosts = [
    { x: 9, y: 8, color: '#FF0000', dir: {x: 0, y: 0}, baseColor: '#FF0000', type: 'blinky', dead: false },
    { x: 8, y: 10, color: '#FFB8FF', dir: {x: 0, y: 0}, baseColor: '#FFB8FF', type: 'pinky', dead: false },
    { x: 9, y: 10, color: '#00FFFF', dir: {x: 0, y: 0}, baseColor: '#00FFFF', type: 'inky', dead: false },
    { x: 10, y: 10, color: '#FFB852', dir: {x: 0, y: 0}, baseColor: '#FFB852', type: 'clyde', dead: false }
  ];

  // Event handlers
  private keyDownHandler: (e: KeyboardEvent) => void;
  private touchStartHandler: (e: TouchEvent) => void;
  private touchEndHandler: (e: TouchEvent) => void;

  private touchStartX = 0;
  private touchStartY = 0;

  // Callbacks
  private onWin?: (result: GameResult) => void;
  private onLose?: (result: GameResult) => void;
  private onUpdate?: (score: number, lives: number, dotsRemaining: number) => void;

  constructor(canvasElement: HTMLCanvasElement) {
    this.canvas = canvasElement;
    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('Could not get 2D context');
    this.ctx = ctx;

    // Set canvas size
    const maxWidth = 500;
    const containerWidth = this.canvas.parentElement?.clientWidth || 500;
    const scaleW = Math.min(1, (containerWidth - 40) / maxWidth);

    this.scale = scaleW;
    this.canvas.width = Math.floor(19 * this.TILE_SIZE * this.scale);
    this.canvas.height = Math.floor(20 * this.TILE_SIZE * this.scale);

    logger.info(`Pac-Man canvas: ${this.canvas.width}x${this.canvas.height}`);

    // Init audio
    this.audioCtx = new (window.AudioContext || window.webkitAudioContext)();

    // Bind event handlers
    this.keyDownHandler = (e: KeyboardEvent) => {
      if (!this.started || !this.isRunning) return;
      switch(e.key) {
        case 'ArrowUp': this.pacman.nextDir = {x: 0, y: -1}; break;
        case 'ArrowDown': this.pacman.nextDir = {x: 0, y: 1}; break;
        case 'ArrowLeft': this.pacman.nextDir = {x: -1, y: 0}; break;
        case 'ArrowRight': this.pacman.nextDir = {x: 1, y: 0}; break;
      }
    };

    this.touchStartHandler = (e: TouchEvent) => {
      e.preventDefault();
      this.touchStartX = e.changedTouches[0].clientX;
      this.touchStartY = e.changedTouches[0].clientY;
    };

    this.touchEndHandler = (e: TouchEvent) => {
      e.preventDefault();
      if (!this.started || !this.isRunning) return;

      const touchEndX = e.changedTouches[0].clientX;
      const touchEndY = e.changedTouches[0].clientY;

      const dx = touchEndX - this.touchStartX;
      const dy = touchEndY - this.touchStartY;

      if (Math.abs(dx) > Math.abs(dy)) {
        if (Math.abs(dx) > 20) {
          this.pacman.nextDir = dx > 0 ? {x: 1, y: 0} : {x: -1, y: 0};
        }
      } else {
        if (Math.abs(dy) > 20) {
          this.pacman.nextDir = dy > 0 ? {x: 0, y: 1} : {x: 0, y: -1};
        }
      }
    };

    this.setupInput();
    this.initMap();
  }

  private setupInput() {
    window.addEventListener('keydown', this.keyDownHandler);
    this.canvas.addEventListener('touchstart', this.touchStartHandler, { passive: false });
    this.canvas.addEventListener('touchend', this.touchEndHandler, { passive: false });
  }

  private initMap() {
    this.map = JSON.parse(JSON.stringify(this.mapLayout));
    this.totalDots = 0;
    for(let y = 0; y < this.map.length; y++) {
      for(let x = 0; x < this.map[0].length; x++) {
        if(this.map[y][x] === 2 || this.map[y][x] === 3) this.totalDots++;
      }
    }
    this.dotsEaten = 0;
  }

  private resetPositions() {
    this.pacman.x = 9;
    this.pacman.y = 16;
    this.pacman.dir = {x: 0, y: 0};
    this.pacman.nextDir = {x: 0, y: 0};
    this.pacman.angle = 0;

    this.ghosts[0].x = 9; this.ghosts[0].y = 8;
    this.ghosts[1].x = 8; this.ghosts[1].y = 10;
    this.ghosts[2].x = 9; this.ghosts[2].y = 10;
    this.ghosts[3].x = 10; this.ghosts[3].y = 10;

    this.ghosts.forEach(g => {
      g.dir = {x: (Math.random() < 0.5 ? -1 : 1), y: 0};
      g.dead = false;
      g.color = g.baseColor;
    });
  }

  /**
   * Start the game
   */
  start(
    onWin?: (result: GameResult) => void,
    onLose?: (result: GameResult) => void,
    onUpdate?: (score: number, lives: number, dotsRemaining: number) => void
  ) {
    this.onWin = onWin;
    this.onLose = onLose;
    this.onUpdate = onUpdate;
    this.isRunning = true;
    this.started = true;

    // Resume audio context
    if (this.audioCtx && this.audioCtx.state === 'suspended') {
      this.audioCtx.resume();
    }

    // Initial update callback
    this.onUpdate?.(this.score, this.lives, this.totalDots - this.dotsEaten);

    this.gameLoop = requestAnimationFrame(() => this.update());
  }

  /**
   * Main game loop
   */
  private update() {
    if (!this.isRunning) return;

    this.updatePacman();
    this.updateGhosts();
    this.render();

    // Check win
    if (this.totalDots - this.dotsEaten === 0) {
      this.win();
      return;
    }

    // Continue loop
    this.gameLoop = requestAnimationFrame(() => this.update());
  }

  private updatePacman() {
    // Try to turn at tile center
    if (Math.abs(this.pacman.x - Math.round(this.pacman.x)) < 0.02 &&
        Math.abs(this.pacman.y - Math.round(this.pacman.y)) < 0.02) {
      this.pacman.x = Math.round(this.pacman.x);
      this.pacman.y = Math.round(this.pacman.y);

      // Try next direction
      if (this.pacman.nextDir.x !== 0 || this.pacman.nextDir.y !== 0) {
        if (!this.isWall(this.pacman.x + this.pacman.nextDir.x, this.pacman.y + this.pacman.nextDir.y)) {
          this.pacman.dir = {...this.pacman.nextDir};
        }
      }

      // Stop if hitting wall
      if (this.isWall(this.pacman.x + this.pacman.dir.x, this.pacman.y + this.pacman.dir.y)) {
        this.pacman.dir = {x: 0, y: 0};
      }
    }

    this.pacman.x += this.pacman.dir.x * this.PACMAN_SPEED;
    this.pacman.y += this.pacman.dir.y * this.PACMAN_SPEED;

    // Wrap around
    if(this.pacman.x < -0.5) this.pacman.x = 18.5;
    if(this.pacman.x > 18.5) this.pacman.x = -0.5;

    // Eat dots
    const px = Math.round(this.pacman.x);
    const py = Math.round(this.pacman.y);

    if (px >= 0 && px < 19 && py >= 0 && py < 20) {
      if (this.map[py][px] === 2) {
        this.map[py][px] = 0;
        this.score += 10;
        this.dotsEaten++;
        if (Date.now() - this.lastChompTime > 200) {
          this.playSound('chomp');
          this.lastChompTime = Date.now();
        }
      } else if (this.map[py][px] === 3) {
        this.map[py][px] = 0;
        this.score += 50;
        this.dotsEaten++;
        this.activatePowerMode();
        this.playSound('power');
      }
    }

    // Mouth animation
    this.pacman.mouthOpen += this.pacman.mouthSpeed;
    if (this.pacman.mouthOpen > 0.25 || this.pacman.mouthOpen < 0) {
      this.pacman.mouthSpeed *= -1;
    }

    // Angle
    if (this.pacman.dir.x === 1) this.pacman.angle = 0;
    if (this.pacman.dir.x === -1) this.pacman.angle = Math.PI;
    if (this.pacman.dir.y === 1) this.pacman.angle = Math.PI / 2;
    if (this.pacman.dir.y === -1) this.pacman.angle = -Math.PI / 2;
  }

  private updateGhosts() {
    if (this.frightenedTimer > 0) this.frightenedTimer--;
    if (this.frightenedTimer === 0 && this.powerMode) {
      this.powerMode = false;
      this.ghosts.forEach(g => { if (!g.dead) g.color = g.baseColor; });
    }

    this.ghosts.forEach(g => {
      if (g.dead) {
        // Return to house
        const dx = 9 - g.x;
        const dy = 9 - g.y;
        const dist = Math.sqrt(dx*dx + dy*dy);
        if (dist < 0.5) {
          g.dead = false;
          g.color = this.powerMode ? '#2222FF' : g.baseColor;
          g.x = 9; g.y = 10;
        } else {
          g.x += (dx/dist) * this.GHOST_SPEED * 2;
          g.y += (dy/dist) * this.GHOST_SPEED * 2;
        }
        return;
      }

      // AI decision at tile center
      if (Math.abs(g.x - Math.round(g.x)) < 0.01 && Math.abs(g.y - Math.round(g.y)) < 0.01) {
        g.x = Math.round(g.x);
        g.y = Math.round(g.y);

        const possibleDirs: Array<{x: number, y: number}> = [];
        [[0,-1],[0,1],[-1,0],[1,0]].forEach(d => {
          if (!this.powerMode && (d[0] === -g.dir.x && d[1] === -g.dir.y)) return;
          if (!this.isWall(g.x + d[0], g.y + d[1])) {
            possibleDirs.push({x: d[0], y: d[1]});
          }
        });

        if (possibleDirs.length === 0) {
          g.dir.x *= -1;
          g.dir.y *= -1;
        } else {
          let chosenDir = possibleDirs[Math.floor(Math.random() * possibleDirs.length)];

          if (possibleDirs.length > 1) {
            if (this.powerMode) {
              // Flee from Pac-Man
              let maxDist = -1;
              possibleDirs.forEach(d => {
                const dist = Math.pow((g.x + d.x) - this.pacman.x, 2) + Math.pow((g.y + d.y) - this.pacman.y, 2);
                if (dist > maxDist) {
                  maxDist = dist;
                  chosenDir = d;
                }
              });
            } else if (Math.random() > 0.2) {
              // Chase Pac-Man
              let minDist = 9999;
              possibleDirs.forEach(d => {
                const dist = Math.pow((g.x + d.x) - this.pacman.x, 2) + Math.pow((g.y + d.y) - this.pacman.y, 2);
                if (dist < minDist) {
                  minDist = dist;
                  chosenDir = d;
                }
              });
            }
          }
          g.dir = chosenDir;
        }
      }

      // Always move the ghost
      const speed = this.powerMode ? this.GHOST_SPEED * 0.6 : this.GHOST_SPEED;
      g.x += g.dir.x * speed;
      g.y += g.dir.y * speed;

      // Wrap
      if(g.x < -0.5) g.x = 18.5;
      if(g.x > 18.5) g.x = -0.5;

      // Collision
      const dist = Math.sqrt(Math.pow(g.x - this.pacman.x, 2) + Math.pow(g.y - this.pacman.y, 2));
      if (dist < 0.8) {
        if (this.powerMode && !g.dead) {
          g.dead = true;
          this.score += 200;
          this.playSound('eat_ghost');
        } else if (!g.dead) {
          this.handleDeath();
        }
      }
    });
  }

  private handleDeath() {
    this.playSound('die');
    this.lives--;
    if (this.lives <= 0) {
      this.lose();
    } else {
      this.resetPositions();
    }
  }

  private activatePowerMode() {
    this.powerMode = true;
    this.frightenedTimer = 400 * 5;
    this.ghosts.forEach(g => {
      if (!g.dead) g.color = '#2222FF';
    });
  }

  private isWall(x: number, y: number): boolean {
    if (x < 0 || x >= 19 || y < 0 || y >= 20) return true;
    const val = this.map[Math.floor(y)][Math.floor(x)];
    return val === 1;
  }

  private render() {
    const ctx = this.ctx;
    const s = this.TILE_SIZE * this.scale;

    // Clear
    ctx.fillStyle = 'black';
    ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    // Draw map
    for(let y = 0; y < this.map.length; y++) {
      for(let x = 0; x < this.map[0].length; x++) {
        const val = this.map[y][x];
        if (val === 1) {
          ctx.fillStyle = '#1919A6';
          ctx.fillRect(x*s, y*s, s, s);
          ctx.fillStyle = 'black';
          ctx.fillRect(x*s + s*0.2, y*s + s*0.2, s*0.6, s*0.6);
        } else if (val === 2) {
          ctx.fillStyle = '#ffb8ae';
          ctx.beginPath();
          ctx.arc(x*s + s/2, y*s + s/2, s*0.15, 0, Math.PI*2);
          ctx.fill();
        } else if (val === 3) {
          ctx.fillStyle = '#ffb8ae';
          if (Math.floor(Date.now() / 200) % 2 === 0) {
            ctx.beginPath();
            ctx.arc(x*s + s/2, y*s + s/2, s*0.35, 0, Math.PI*2);
            ctx.fill();
          }
        } else if (val === 4) {
          ctx.fillStyle = 'pink';
          ctx.fillRect(x*s, y*s + s*0.4, s, s*0.2);
        }
      }
    }

    // Draw ghosts
    this.ghosts.forEach(g => {
      const gx = g.x * s;
      const gy = g.y * s;

      if (g.dead) {
        this.drawEyes(gx, gy, s);
      } else {
        ctx.fillStyle = g.color;
        ctx.beginPath();
        ctx.arc(gx + s/2, gy + s/2 - s*0.1, s*0.4, Math.PI, 0);
        ctx.lineTo(gx + s*0.9, gy + s*0.9);
        ctx.lineTo(gx + s*0.7, gy + s*0.8);
        ctx.lineTo(gx + s*0.5, gy + s*0.9);
        ctx.lineTo(gx + s*0.3, gy + s*0.8);
        ctx.lineTo(gx + s*0.1, gy + s*0.9);
        ctx.lineTo(gx + s*0.1, gy + s/2);
        ctx.fill();
        this.drawEyes(gx, gy, s);
      }
    });

    // Draw Pac-Man
    const px = this.pacman.x * s + s/2;
    const py = this.pacman.y * s + s/2;

    ctx.fillStyle = '#FFFF00';
    ctx.beginPath();
    ctx.moveTo(px, py);
    const mouthSize = this.pacman.mouthOpen * Math.PI;
    ctx.arc(px, py, s*0.4, this.pacman.angle + mouthSize, this.pacman.angle + 2*Math.PI - mouthSize);
    ctx.fill();

    // Draw HUD
    this.drawHUD();
  }

  private drawEyes(x: number, y: number, s: number) {
    this.ctx.fillStyle = 'white';
    this.ctx.beginPath();
    this.ctx.arc(x + s*0.35, y + s*0.4, s*0.12, 0, Math.PI*2);
    this.ctx.arc(x + s*0.65, y + s*0.4, s*0.12, 0, Math.PI*2);
    this.ctx.fill();

    this.ctx.fillStyle = 'blue';
    this.ctx.beginPath();
    this.ctx.arc(x + s*0.35 + s*0.04, y + s*0.4, s*0.05, 0, Math.PI*2);
    this.ctx.arc(x + s*0.65 + s*0.04, y + s*0.4, s*0.05, 0, Math.PI*2);
    this.ctx.fill();
  }

  private drawHUD() {
    const ctx = this.ctx;

    // Score
    ctx.fillStyle = '#FFFF00';
    ctx.font = `bold ${12 * this.scale}px Arial`;
    ctx.textAlign = 'left';
    ctx.fillText(`SCORE: ${this.score}`, 10, 15 * this.scale);

    // Lives
    ctx.textAlign = 'right';
    ctx.fillText(`LIVES: ${this.lives}`, this.canvas.width - 10, 15 * this.scale);

    // Dots remaining
    ctx.textAlign = 'center';
    ctx.fillStyle = '#FFB8AE';
    ctx.fillText(`DOTS: ${this.totalDots - this.dotsEaten}`, this.canvas.width / 2, 15 * this.scale);

    // Notify external UI of state changes
    this.onUpdate?.(this.score, this.lives, this.totalDots - this.dotsEaten);
  }

  private playSound(type: 'chomp' | 'eat_ghost' | 'power' | 'die' | 'win') {
    if (!this.audioCtx) return;

    const osc = this.audioCtx.createOscillator();
    const gainNode = this.audioCtx.createGain();
    osc.connect(gainNode);
    gainNode.connect(this.audioCtx.destination);

    const now = this.audioCtx.currentTime;

    if (type === 'chomp') {
      osc.type = 'sine';
      osc.frequency.setValueAtTime(150, now);
      osc.frequency.exponentialRampToValueAtTime(100, now + 0.1);
      gainNode.gain.setValueAtTime(0.1, now);
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.1);
      osc.start(now);
      osc.stop(now + 0.1);
    } else if (type === 'eat_ghost') {
      osc.type = 'square';
      osc.frequency.setValueAtTime(800, now);
      osc.frequency.exponentialRampToValueAtTime(1200, now + 0.1);
      gainNode.gain.setValueAtTime(0.1, now);
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.2);
      osc.start(now);
      osc.stop(now + 0.2);
    } else if (type === 'power') {
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(300, now);
      osc.frequency.linearRampToValueAtTime(600, now + 0.2);
      osc.frequency.linearRampToValueAtTime(300, now + 0.4);
      gainNode.gain.setValueAtTime(0.1, now);
      gainNode.gain.linearRampToValueAtTime(0, now + 0.4);
      osc.start(now);
      osc.stop(now + 0.4);
    } else if (type === 'die') {
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(500, now);
      osc.frequency.exponentialRampToValueAtTime(50, now + 1);
      gainNode.gain.setValueAtTime(0.2, now);
      gainNode.gain.linearRampToValueAtTime(0, now + 1);
      osc.start(now);
      osc.stop(now + 1);
    } else if (type === 'win') {
      const notes = [523.25, 659.25, 783.99, 1046.50];
      let time = now;
      notes.forEach(note => {
        const o = this.audioCtx!.createOscillator();
        const g = this.audioCtx!.createGain();
        o.connect(g);
        g.connect(this.audioCtx!.destination);
        o.type = 'square';
        o.frequency.value = note;
        g.gain.setValueAtTime(0.1, time);
        g.gain.exponentialRampToValueAtTime(0.01, time + 0.2);
        o.start(time);
        o.stop(time + 0.2);
        time += 0.15;
      });
    }
  }

  private win() {
    this.stop();
    this.playSound('win');
    const result: GameResult = {
      won: true,
      score: this.score,
      dotsRemaining: 0
    };
    this.onWin?.(result);
  }

  private lose() {
    this.stop();
    const result: GameResult = {
      won: false,
      score: this.score,
      dotsRemaining: this.totalDots - this.dotsEaten
    };
    this.onLose?.(result);
  }

  stop() {
    this.isRunning = false;
    if (this.gameLoop) {
      cancelAnimationFrame(this.gameLoop);
      this.gameLoop = null;
    }
  }

  dispose() {
    this.stop();
    window.removeEventListener('keydown', this.keyDownHandler);
    this.canvas.removeEventListener('touchstart', this.touchStartHandler);
    this.canvas.removeEventListener('touchend', this.touchEndHandler);
    logger.info('Pac-Man game disposed');
  }

  getScore(): number {
    return this.score;
  }

  getLives(): number {
    return this.lives;
  }
}
